import type { FCAData } from "@/types/fca";

interface Props {
  data: FCAData;
}

const quickStats = [
  { label: "DAP", value: "", unit: "cm", icon: "ri-ruler-line", key: "dap" as const, color: "accent" },
  { label: "Altura", value: "", unit: "m", icon: "ri-arrow-up-line", key: "altura" as const, color: "accent" },
  { label: "Diâmetro de Copa", value: "", unit: "m", icon: "ri-contrast-2-line", key: "diametroCopa" as const, color: "accent" },
  { label: "Última Vistoria", value: "", unit: "", icon: "ri-calendar-check-line", key: "ultimaVistoria" as const, color: "primary" },
  { label: "Fotos", value: "", unit: "", icon: "ri-camera-line", key: "fotosCount" as const, color: "primary" },
  { label: "Manejo Recomendado", value: "", unit: "", icon: "ri-tools-line", key: "manejoRecomendado" as const, color: "warning" },
];

export default function TreeQuickStats({ data }: Props) {
  // Sem vistoria a medida nao existe: mostra "—", nunca "0 cm".
  const formatDate = (dateStr: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr ?? "")) return "—";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };
  const measure = (v: number | null | undefined) => (v && v > 0 ? `${v}` : "—");

  const getValue = (key: string) => {
    switch (key) {
      case "dap": return measure(data.dap);
      case "altura": return measure(data.altura);
      case "diametroCopa": return measure(data.diametroCopa);
      case "ultimaVistoria": return formatDate(data.ultimaVistoria);
      case "fotosCount": return `${data.fotosCount}`;
      case "manejoRecomendado": return data.manejoRecomendado || "—";
      default: return "";
    }
  };

  const getUnit = (key: string) => {
    if (getValue(key) === "—") return "";
    switch (key) {
      case "dap": return "cm";
      case "altura": return "m";
      case "diametroCopa": return "m";
      default: return "";
    }
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case "accent": return { bg: "bg-accent-50", icon: "text-accent-600", bar: "bg-accent-500" };
      case "primary": return { bg: "bg-primary-50", icon: "text-primary-600", bar: "bg-primary-500" };
      case "warning": return { bg: "bg-warning-50", icon: "text-warning-600", bar: "bg-warning-500" };
      default: return { bg: "bg-background-100", icon: "text-foreground-500", bar: "bg-foreground-500" };
    }
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {quickStats.map((stat) => {
        const color = getColorClasses(stat.color);
        const rawValue = getValue(stat.key);
        const unit = getUnit(stat.key);

        return (
          <div key={stat.key} className="bg-white rounded-card border border-background-200/70 p-3 overflow-hidden relative">
            <div className={`absolute top-0 left-0 right-0 h-[3px] ${color.bar}`}></div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`w-7 h-7 rounded-md ${color.bg} flex items-center justify-center`}>
                <i className={`${stat.icon} ${color.icon} text-sm`}></i>
              </span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-foreground-900 font-heading">{rawValue}</span>
              {unit && <span className="text-xs text-foreground-500">{unit}</span>}
            </div>
            <p className="text-[11px] text-foreground-500 mt-0.5 leading-tight">{stat.label}</p>
          </div>
        );
      })}
    </div>
  );
}